import React, { useEffect, useState } from 'react';
import Button from '../UI/Button';
import Card from '../UI/Card';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Contacts, SocialLink } from '../../api/services/platformSettings.service';

interface ContactsTabProps {
  value: Contacts;
  onSave: (contacts: Contacts) => Promise<void>;
  saving: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const ContactsTab: React.FC<ContactsTabProps> = ({ value, onSave, saving }) => {
  const [form, setForm] = useState<Contacts>(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(value);
    setDirty(false);
  }, [value]);

  const set = <K extends keyof Contacts>(key: K, v: Contacts[K]) => {
    setForm((prev) => ({ ...prev, [key]: v }));
    setDirty(true);
  };

  const updateSocial = (i: number, patch: Partial<SocialLink>) => {
    setForm((prev) => ({
      ...prev,
      socials: prev.socials.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
    setDirty(true);
  };

  const addSocial = () => {
    setForm((prev) => ({ ...prev, socials: [...prev.socials, { label: '', url: '' }] }));
    setDirty(true);
  };

  const removeSocial = (i: number) => {
    setForm((prev) => ({
      ...prev,
      socials: prev.socials.filter((_, idx) => idx !== i),
    }));
    setDirty(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Phone</label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="+251 ..."
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="support@example.com"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Telegram Channel</label>
            <input
              type="text"
              value={form.telegramChannel}
              onChange={(e) => set('telegramChannel', e.target.value)}
              placeholder="@channel"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Telegram Support</label>
            <input
              type="text"
              value={form.telegramSupport}
              onChange={(e) => set('telegramSupport', e.target.value)}
              placeholder="@support"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Website</label>
            <input
              type="url"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://..."
              className={inputClass}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900">Social Links</h2>
          <button
            onClick={addSocial}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add Link
          </button>
        </div>

        {form.socials.length === 0 && (
          <p className="text-xs text-gray-500">No social links yet.</p>
        )}

        <div className="space-y-2">
          {form.socials.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Label (e.g. Facebook)"
                value={s.label}
                onChange={(e) => updateSocial(i, { label: e.target.value })}
                className={`${inputClass} flex-1`}
              />
              <input
                type="url"
                placeholder="https://..."
                value={s.url}
                onChange={(e) => updateSocial(i, { url: e.target.value })}
                className={`${inputClass} flex-[2]`}
              />
              <button
                onClick={() => removeSocial(i)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500 flex-shrink-0"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" disabled={!dirty || saving} onClick={() => onSave(form)}>
          {saving ? 'Saving...' : 'Save Contacts'}
        </Button>
      </div>
    </div>
  );
};

export default ContactsTab;